import { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { Ok, Result } from 'ts-results'

import { ConnectionSetupRoutes } from 'app/application/types'
import { Form } from 'app/common/presentation'
import { useConnectionsApi } from 'app/connections/application'
import {
  AvailableOrInstalledConnection,
  ConnectionConfig,
  ConnectionName,
  ShopifyShop,
  SupportedAccount,
  isInstalledConnection,
} from 'app/connections/types'

import Accounts from './Accounts'
import Authorization from './Authorization'
import InputWithExampleFormRow from './InputWithExampleFormRow/InputWithExampleFormRow'
import { AuthorizationData, SettingsProps } from './Settings'
import styles from './Setup.module.scss'

export type AuthorizationState = 'OUTDATED' | 'OUTDATED_OR_VALID' | 'PENDING' | 'UNKOWN' | 'VALID'

export interface GenericSettingsProps extends SettingsProps, PropsWithChildren {
  authorizationData: AuthorizationData
  handleSaveAuthorization: (
    authorizationData: AuthorizationData,
  ) => Promise<Result<void, string | void>>
  onConnectionConfigUpdate: (key: string, value: SupportedAccount[] | string) => void
  setAuthorizationData: (authorizationData: AuthorizationData) => void
  connection: AvailableOrInstalledConnection
  connectionConfig: ConnectionConfig
}

type AccountsRow = { key: 'accounts'; value: SupportedAccount[] | undefined }
type ShopsRow = { key: 'shops'; value: ShopifyShop[] }
type GenericRow = { key: Exclude<string, 'accounts' | 'shops'>; value: string }

type RowData = AccountsRow | ShopsRow | GenericRow

const isAccountsRow = (row: RowData): row is AccountsRow =>
  ['accounts', 'advertisers'].includes(row.key)
const isShopsRow = (row: RowData): row is { key: 'shops'; value: ShopifyShop[] } =>
  row.key === 'shops'

const hasMultiStepConnectionSetup: ConnectionName[] = [
  'amazon',
  'criteo',
  'dv360',
  'facebook',
  'googleAdwords',
  'googleAnalytics',
]

const hasOAuthCheck = (connectionName: ConnectionName) =>
  ['facebook', 'googleAdwords', 'googleAnalytics'].includes(connectionName)

// eslint-disable-next-line react-refresh/only-export-components
export const isConfigured = (conn: AvailableOrInstalledConnection) => {
  switch (conn.name) {
    case 'amazon':
      // amazon only stores the token for now, there is nothing to select
      return true
    case 'criteo':
    case 'dv360':
      return conn.config.advertisers.length > 0
    case 'facebook':
    case 'googleAdwords':
      return conn.config.accounts.length > 0
    case 'googleAnalytics':
      return !!conn.config.propertyId
    default:
      return false
  }
}

const hasExampleValue: ConnectionName[] = ['facebook', 'googleAdwords']

const getUrlParamValue = (key: 'adId' | 'campaignId') => {
  switch (key) {
    case 'adId':
      return '{{adset.id}}'
    case 'campaignId':
      return '{campaignid}'
  }
}

const GenericSettings = ({
  authorizationData,
  children,
  handleSaveAuthorization,
  onConnectionConfigUpdate: onConnectionConfigUpdate,
  onConnectionConfigValidationUpdate,
  setAuthorizationData,
  connection,
  connectionConfig,
}: GenericSettingsProps) => {
  const { t } = useTranslation('connections')

  const { getOAuth } = useConnectionsApi(connection.name)

  const [authorizationState, setAuthorizationState] = useState<AuthorizationState>('UNKOWN')

  const multiStepConnectionSetupConfig = hasMultiStepConnectionSetup.includes(connection.name)

  useEffect(() => {
    if (connection.authConfig) {
      if (isInstalledConnection(connection)) {
        if (hasOAuthCheck(connection.name)) {
          const isOutdated = ['error', 'disabled'].includes(connection.status)
          if (isOutdated) {
            setAuthorizationState('OUTDATED')
          } else {
            void getOAuth().then((result) => {
              if (result.ok) {
                setAuthorizationState('VALID')
              } else {
                setAuthorizationState('OUTDATED')
              }
            })
          }
        } else {
          setAuthorizationState('OUTDATED_OR_VALID')
        }
      } else {
        setAuthorizationState(authorizationData.authorizationCode ? 'VALID' : 'PENDING')
      }
    }
  }, [connection, getOAuth, authorizationData.authorizationCode, setAuthorizationState])

  useEffect(() => {
    const isValidConfig = () => {
      if (Object.values(connectionConfig).some((value) => !value)) return false

      if (connection.authConfig && !['OUTDATED_OR_VALID', 'VALID'].includes(authorizationState)) {
        return false
      }

      return true
    }

    onConnectionConfigValidationUpdate(isValidConfig())
  }, [
    authorizationState,
    onConnectionConfigValidationUpdate,
    connection.authConfig,
    connectionConfig,
  ])

  if (multiStepConnectionSetupConfig) {
    if (!isInstalledConnection(connection)) {
      return <Navigate to={ConnectionSetupRoutes.SignIn} replace state={{ connection }} />
    }
    if (!isConfigured(connection)) {
      return <Navigate to={ConnectionSetupRoutes.SelectWorkspaces} replace state={{ connection }} />
    }
  }

  const handleOnChange = (key: string) => (value: string) => onConnectionConfigUpdate(key, value)

  const renderFormRow = (row: RowData) => {
    if (isAccountsRow(row)) {
      const selectedAccounts = row.value ?? []

      return <Accounts key={row.key} accounts={selectedAccounts} connectionName={connection.name} />
    } else if (isShopsRow(row)) {
      return row.value.map((shop) => (
        <Form.InputFormRow
          key={row.key}
          description={t(`setup.${connection.name}.config.shop.description`)}
          title={t(`setup.${connection.name}.config.shop.title`)}
          value={shop}
        />
      ))
    } else {
      if (hasExampleValue.includes(connection.name)) {
        switch (row.key) {
          case 'adId':
          case 'campaignId':
            return (
              <InputWithExampleFormRow
                key={row.key}
                data={row}
                example={
                  <div>
                    https://yourdomain.com/landing?
                    <span className={styles.exampleHighlight}>{row.value}</span>=
                    {getUrlParamValue(row.key)}
                  </div>
                }
                exampleHint={t('setup.trackingUrlHint')}
                handleOnChange={handleOnChange(row.key)}
                connection={connection}
              />
            )
        }
      }

      return (
        <Form.InputFormRow
          key={row.key}
          description={t(`setup.${connection.name}.config.${row.key}.description`)}
          onChange={
            connection.readonly || connection.readonlyConfig.includes(row.key)
              ? undefined
              : handleOnChange(row.key)
          }
          placeholder={t(`setup.${connection.name}.config.${row.key}.title`)}
          title={t(`setup.${connection.name}.config.${row.key}.title`)}
          value={row.value}
        />
      )
    }
  }

  const handleSetAuthorizationData = (authorizationData: AuthorizationData) => {
    setAuthorizationData(authorizationData)
    return Promise.resolve(Ok(undefined))
  }

  return (
    <div className={styles.form}>
      {connection.authConfig && authorizationData && (
        <Authorization
          authorizationState={authorizationState}
          redirectUri={authorizationData.redirectUri}
          setAuthorizationData={
            multiStepConnectionSetupConfig ? handleSaveAuthorization : handleSetAuthorizationData
          }
          connection={connection}
        />
      )}
      {(!connection.authConfig || ['OUTDATED_OR_VALID', 'VALID'].includes(authorizationState)) && (
        <>
          {Object.entries(connectionConfig).map(
            ([key, value]: [string, SupportedAccount[] | string | ShopifyShop[]]) =>
              renderFormRow({ key, value } as RowData),
          )}
          {children}
        </>
      )}
    </div>
  )
}

export default GenericSettings
